import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, BookOpen, LogOut } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { getOnboardingState, OpleiderStap } from '../services/opleiderOnboardingApi';
import { StapAccountFormulier, StapVerificatie } from '../components/opleiderOnboarding/StapAccount';
import { StapProfiel } from '../components/opleiderOnboarding/StapProfiel';
import { StapKvk, SbiWeigering } from '../components/opleiderOnboarding/StapKvk';
import { StapContract } from '../components/opleiderOnboarding/StapContract';
import { StapStripe, GoLive } from '../components/opleiderOnboarding/StapStripe';
import { Kaart, Knop } from '../components/opleiderOnboarding/ui';

export const OPLEIDER_ONBOARDING_QUERY = ['opleider-onboarding'] as const;

/**
 * Integrale opleider onboarding-wizard (full-screen, donker). Geen toegang tot
 * de cockpit (Menu 1 t/m 5) tot de server ONBOARDED_ACTIVE meldt; de stap komt
 * altijd van de server (GET /api/opleider/onboarding).
 */
export const OpleiderOnboardingPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const { isAuthenticated, user, logout } = useAuthStore();
  const isOpleider = isAuthenticated && String(user?.role) === 'opleider';
  const terugUitStripe = params.get('stripe') === 'return';

  const { data: state, isLoading } = useQuery({
    queryKey: OPLEIDER_ONBOARDING_QUERY,
    queryFn: () => getOnboardingState(terugUitStripe),
    enabled: isOpleider,
    // Stap 1: wachten op de verificatieklik. Stap 5: wachten op Stripe na terugkeer.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (d?.stap === 1) return 4000;
      if (d?.stap === 5 && terugUitStripe) return 5000;
      return false;
    },
    refetchIntervalInBackground: false,
  });

  // Feestelijk go-live alleen als de activatie tijdens deze sessie gebeurde.
  const vorigeStap = useRef<OpleiderStap | null>(null);
  const [feest, setFeest] = useState(false);
  useEffect(() => {
    if (!state) return;
    if (state.stap === 'actief' && (typeof vorigeStap.current === 'number' || terugUitStripe)) setFeest(true);
    vorigeStap.current = state.stap;
  }, [state, terugUitStripe]);

  const ververs = () => queryClient.invalidateQueries({ queryKey: OPLEIDER_ONBOARDING_QUERY });
  const uitloggen = () => {
    logout();
    queryClient.clear();
    navigate('/login');
  };

  // Al actief bij binnenkomst (niet tijdens deze sessie geactiveerd): direct naar de cockpit.
  if (isOpleider && state?.stap === 'actief' && !feest && typeof vorigeStap.current !== 'number' && !terugUitStripe) {
    return <Navigate to="/opleider/dashboard" replace />;
  }

  const stapNummer = !isOpleider ? 1 : state?.stap === 'geweigerd' ? 3 : typeof state?.stap === 'number' ? state.stap : 5;

  let inhoud: React.ReactNode;
  if (!isAuthenticated) {
    inhoud = <StapAccountFormulier onAangemaakt={() => navigate('/opleider/onboarding', { replace: true })} />;
  } else if (!isOpleider) {
    inhoud = (
      <Kaart className="text-center">
        <p className="text-slate-300">
          {t('opleiderWizard.andereRol', { defaultValue: 'U bent ingelogd met een ander accounttype. Log uit om een opleidersaccount aan te maken.' })}
        </p>
        <Knop variant="secundair" className="mt-5" onClick={uitloggen}>
          {t('opleiderWizard.weigering.uitloggen', { defaultValue: 'Uitloggen' })}
        </Knop>
      </Kaart>
    );
  } else if (isLoading || !state) {
    inhoud = <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-sky-400" role="status" aria-label="Laden" />;
  } else if (state.stap === 1) {
    inhoud = <StapVerificatie email={state.email} />;
  } else if (state.stap === 2) {
    inhoud = <StapProfiel state={state} onKlaar={ververs} />;
  } else if (state.stap === 3) {
    inhoud = <StapKvk state={state} onKlaar={ververs} onGeweigerd={ververs} />;
  } else if (state.stap === 'geweigerd') {
    inhoud = <SbiWeigering state={state} onUitloggen={uitloggen} />;
  } else if (state.stap === 4) {
    inhoud = <StapContract state={state} onKlaar={ververs} />;
  } else {
    inhoud = <StapStripe state={state} terugUitStripe={terugUitStripe} />;
  }

  const STAPPEN = [
    t('opleiderWizard.rail.account', { defaultValue: 'Account' }),
    t('opleiderWizard.rail.profiel', { defaultValue: 'Instituutprofiel' }),
    t('opleiderWizard.rail.kvk', { defaultValue: 'KvK & SBI' }),
    t('opleiderWizard.rail.contract', { defaultValue: 'Overeenkomst' }),
    t('opleiderWizard.rail.stripe', { defaultValue: 'Stripe Connect' }),
  ];

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200 [color-scheme:dark]">
      <style>{'@keyframes blg-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } } @media (prefers-reduced-motion: reduce) { .blg-in { animation: none !important } }'}</style>
      <div className="pointer-events-none fixed inset-x-0 top-0 h-80 bg-[radial-gradient(60%_100%_at_50%_0%,rgba(56,189,248,0.12),transparent)]" aria-hidden="true" />

      <header className="relative mx-auto flex max-w-4xl items-center justify-between px-4 py-6">
        <div className="flex items-center gap-2 font-bold text-white">
          <BookOpen className="h-6 w-6 text-sky-400" aria-hidden="true" />
          BeLagom <span className="font-medium text-slate-400">Academy · {t('opleiderWizard.voorOpleiders', { defaultValue: 'voor opleiders' })}</span>
        </div>
        {isAuthenticated && (
          <button onClick={uitloggen} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-slate-400 hover:bg-white/5 hover:text-white">
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('opleiderWizard.weigering.uitloggen', { defaultValue: 'Uitloggen' })}
          </button>
        )}
      </header>

      <nav aria-label={t('opleiderWizard.rail.label', { defaultValue: 'Onboardingstappen' })} className="relative mx-auto max-w-4xl px-4">
        <ol className="flex items-center">
          {STAPPEN.map((label, i) => {
            const n = i + 1;
            const klaar = n < stapNummer || state?.stap === 'actief';
            const actief = n === stapNummer && state?.stap !== 'actief';
            const geweigerd = actief && state?.stap === 'geweigerd';
            return (
              <li key={label} className="flex flex-1 items-center last:flex-none" aria-current={actief ? 'step' : undefined}>
                <div className="flex flex-col items-center gap-1.5">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ring-1 transition ${
                      geweigerd
                        ? 'bg-rose-500 text-white ring-rose-400'
                        : klaar
                          ? 'bg-emerald-400 text-slate-950 ring-emerald-300'
                          : actief
                            ? 'bg-sky-400 text-slate-950 ring-sky-300 shadow-[0_0_24px_rgba(56,189,248,0.5)]'
                            : 'bg-white/5 text-slate-500 ring-white/10'
                    }`}
                  >
                    {klaar ? <Check className="h-4 w-4" aria-hidden="true" /> : n}
                  </span>
                  <span className={`hidden text-xs font-medium sm:block ${actief ? 'text-white' : klaar ? 'text-emerald-300' : 'text-slate-500'}`}>{label}</span>
                </div>
                {n < STAPPEN.length && <span className={`mx-2 mb-0 h-px flex-1 sm:mb-5 ${klaar ? 'bg-emerald-400/60' : 'bg-white/10'}`} aria-hidden="true" />}
              </li>
            );
          })}
        </ol>
        <p className="mt-3 text-center text-sm font-medium text-white sm:hidden">{STAPPEN[stapNummer - 1]}</p>
      </nav>

      <main className="relative mx-auto max-w-3xl px-4 pb-16 pt-8">
        <div key={String(state?.stap ?? (isAuthenticated ? 'laden' : 'account'))} className="blg-in" style={{ animation: 'blg-in .35s ease-out both' }}>
          {inhoud}
        </div>
      </main>

      {feest && (
        <GoLive
          onStart={() => {
            queryClient.invalidateQueries({ queryKey: OPLEIDER_ONBOARDING_QUERY });
            queryClient.invalidateQueries({ queryKey: ['provider-me'] });
            navigate('/opleider/trainings/nieuw', { replace: true });
          }}
        />
      )}
    </div>
  );
};

export default OpleiderOnboardingPage;
